import React, { useEffect, useRef } from 'react';
import { T } from '../../utils/adminTheme';

interface SlidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  children: React.ReactNode;
}

const SlidePanel: React.FC<SlidePanelProps> = ({ open, onClose, title, width = 480, children }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape-Taste schließt Panel
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Body-Scroll sperren wenn offen
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <style>{`
        @keyframes fade-overlay {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .slide-panel-overlay {
          animation: fade-overlay 0.2s ease;
        }
      `}</style>

      {/* Overlay */}
      {open && (
        <div
          className="slide-panel-overlay"
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,17,20,0.45)',
            backdropFilter: 'blur(2px)',
            zIndex: 300,
          }}
        />
      )}

      {/* Drawer */}
      <div
        ref={panelRef}
        data-slide-panel-title={title}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: Math.min(width, window.innerWidth),
          background: T.surface,
          borderLeft: `1px solid ${T.line}`,
          zIndex: 301,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          transform: open ? 'none' : 'translateX(100%)',
          transition: 'none',
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px',
          borderBottom: `1px solid ${T.line}`,
          flexShrink: 0,
          background: T.surface2,
        }}>
          <h2 style={{
            margin: 0,
            fontFamily: T.display,
            fontStyle: 'italic',
            fontSize: 20,
            fontWeight: 500,
            color: T.text,
          }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: T.muted, fontSize: 20, lineHeight: 1, padding: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: T.r.sm,
            }}
            title="Schließen"
          >
            ✕
          </button>
        </div>

        {/* Inhalt mit Scroll */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {children}
        </div>
      </div>
    </>
  );
};

export default SlidePanel;
